import { Code } from "@connectrpc/connect";
import { useQuery } from "@connectrpc/connect-query";
import { keepPreviousData } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { formatNumber, type Lang } from "@yatayat/core/money";
import { ConsoleService, type DirectionForecast, type GetFestivalForecastResponse } from "@yatayat/gen/yatayat/console/v1/console_pb";

import { formatBp, formatUplift } from "~/components/charts/format";
import { StatTile } from "~/components/charts/StatTile";
import { ForecastDirection } from "~/components/console/ForecastDirection";
import { useStaffSessionExpiry } from "~/components/OperatorTrip";
import { Empty, Loading, QueryError } from "~/components/QueryState";
import { explainVariant } from "~/lib/forecast";
import { useI18n, type MessageKey } from "~/lib/i18n";
import { formatIsoDate, weekdayLabels } from "~/lib/labels";
import { errorCode, retryTransient } from "~/lib/rpc/errors";
import { searchString } from "~/lib/search";
import { ui } from "~/lib/ui";

type ForecastSearch = { festival?: string; corridor?: string };

// The storyline's corridor (POC plan §4.7), shown first when the authority runs it.
const defaultCorridor = "KTM-BTL";

export const Route = createFileRoute("/console/forecast")({
  validateSearch: (search: Record<string, unknown>): ForecastSearch => ({
    festival: searchString(search.festival),
    corridor: searchString(search.corridor),
  }),
  component: ConsoleForecast,
});

const corridorPattern = /^[A-Z0-9-]{3,32}$/;
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const fieldClass = "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm sm:w-auto dark:border-slate-700 dark:bg-slate-900";

function ConsoleForecast() {
  const { t, lang } = useI18n();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const update = (patch: ForecastSearch) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true, resetScroll: false });

  const festivals = useQuery(ConsoleService.method.listFestivals, {}, { retry: retryTransient });
  const corridors = useQuery(ConsoleService.method.listCorridors, {}, { retry: retryTransient });
  const festivalList = festivals.data?.festivals ?? [];
  const corridorList = corridors.data?.corridors ?? [];

  const festivalId = search.festival && uuidPattern.test(search.festival) ? search.festival : (festivals.data?.nextId ?? "");
  const corridor =
    search.corridor && corridorPattern.test(search.corridor)
      ? search.corridor
      : corridorList.some((c) => c.code === defaultCorridor)
        ? defaultCorridor
        : (corridorList[0]?.code ?? "");

  const forecast = useQuery(
    ConsoleService.method.getFestivalForecast,
    { festivalId, corridorCode: corridor },
    { enabled: festivalId !== "" && corridor !== "", retry: retryTransient, placeholderData: keepPreviousData },
  );
  const unauthenticated = useStaffSessionExpiry(festivals.error ?? corridors.error ?? forecast.error);
  const data = forecast.data;
  const busy = forecast.isPlaceholderData;

  let body;
  if (festivals.isPending || corridors.isPending) {
    body = <Loading />;
  } else if (festivals.isError || corridors.isError) {
    const retry = () => void Promise.all([festivals.refetch(), corridors.refetch()]);
    body = !unauthenticated && <QueryError onRetry={retry} busy={festivals.isFetching || corridors.isFetching} />;
  } else if (festivalId === "" || corridor === "") {
    body = <Empty>{t("fcNothingToForecast")}</Empty>;
  } else if (forecast.isPending) {
    body = <Loading />;
  } else if (!data) {
    const code = forecast.error ? errorCode(forecast.error) : Code.Unknown;
    body =
      !unauthenticated &&
      (code === Code.FailedPrecondition || code === Code.NotFound ? (
        <Empty>{t(code === Code.NotFound ? "fcNotFound" : "fcNotForecastable")}</Empty>
      ) : (
        <QueryError onRetry={() => void forecast.refetch()} busy={forecast.isFetching} />
      ));
  } else {
    body = <ForecastBody data={data} busy={busy} />;
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="min-w-0">
          <h1 className={ui.h1}>{t("fcTitle")}</h1>
          <p className="text-slate-600 dark:text-slate-400">{t("fcIntro")}</p>
        </div>
        <div className="flex flex-wrap items-end gap-4 rounded-xl bg-slate-50 p-3 dark:bg-slate-900">
          <label className="block w-full text-sm sm:w-auto">
            <span className="mb-1 block font-medium">{t("consoleFestival")}</span>
            <select value={festivalId} onChange={(e) => update({ festival: e.target.value || undefined })} className={fieldClass}>
              {festivalId === "" && <option value="">{t("fcChooseFestival")}</option>}
              {festivalList.map((f) => (
                <option key={f.id} value={f.id} disabled={!f.forecastable}>
                  {f.forecastable ? f.name?.[lang] || f.code : t("fcNoHistory", { name: f.name?.[lang] || f.code })}
                </option>
              ))}
            </select>
          </label>
          <label className="block w-full text-sm sm:w-auto">
            <span className="mb-1 block font-medium">{t("consoleCorridor")}</span>
            <select value={corridor} onChange={(e) => update({ corridor: e.target.value || undefined })} className={fieldClass}>
              {corridorList.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name?.[lang] || c.code}
                </option>
              ))}
              {corridor !== "" && !corridorList.some((c) => c.code === corridor) && <option value={corridor}>{corridor}</option>}
            </select>
          </label>
        </div>
      </div>
      {body}
    </div>
  );
}

function ForecastBody({ data, busy }: { data: GetFestivalForecastResponse; busy: boolean }) {
  const { t, lang } = useI18n();
  const n = (v: number) => formatNumber(v, lang);
  const total = data.directions.reduce((s, d) => s + d.totalExtraDepartures, 0);
  const ahead = data.directions.reduce((s, d) => s + d.days.reduce((x, day) => x + (day.hasActual ? 0 : day.extraDepartures), 0), 0);
  const festival = data.festival;
  const previous = data.previous;
  const load = formatBp(data.targetLoadBp, lang);
  const variant = explainVariant(
    { departures: 0, extraDepartures: 0, upliftBp: 10000 },
    data.baselineTo,
    festival?.startsOn ?? "",
  );

  return (
    <div className="space-y-6">
      <section className={`space-y-3 rounded-xl border border-slate-200 p-4 dark:border-slate-800 ${busy ? "opacity-60" : ""}`}>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {festival?.name?.[lang]} · {data.corridorName?.[lang] || data.corridorCode} ·{" "}
          {t("consoleRangeLabel", { from: formatIsoDate(festival?.startsOn ?? "", lang), to: formatIsoDate(festival?.endsOn ?? "", lang) })}
        </p>
        <p className="text-3xl font-semibold text-ink dark:text-ink-dark">
          {total > 0 ? t(total === 1 ? "fcRecommendOne" : "fcRecommend", { n: n(total) }) : t("fcNoExtraNeeded")}
        </p>
        {ahead !== total && <p className="text-sm font-medium">{t("fcStillAhead", { n: n(ahead) })}</p>}
        <p className="text-sm">
          {t(variant.baseline === "latest" ? "fcExplainLatest" : "fcExplainBefore", {
            weeks: n(data.baselineWeeks),
            from: formatIsoDate(data.baselineFrom, lang),
            to: formatIsoDate(data.baselineTo, lang),
            previous: previous?.name?.[lang] || "",
            load,
          })}
        </p>
        <p className="text-sm font-medium text-brand dark:text-brand-dark">{t("fcNotGranted")}</p>
      </section>

      <dl className={`grid gap-4 sm:grid-cols-2 ${busy ? "opacity-60" : ""}`}>
        {data.directions.map((d) => (
          <StatTile key={d.routeCode} label={d.routeName?.[lang] || d.routeCode} value={d.totalExtraDepartures > 0 ? `+${n(d.totalExtraDepartures)}` : "0"}>
            <span className="block">{t("fcTileExtra")}</span>
            <WorkedExample dir={d} data={data} lang={lang} />
          </StatTile>
        ))}
      </dl>

      {data.directions.map((d) => (
        // Keyed by what was asked, so the what-if starts afresh for another festival or corridor.
        <ForecastDirection key={`${festival?.id}-${d.routeCode}`} direction={d} targetLoadBp={data.targetLoadBp} busy={busy} />
      ))}
    </div>
  );
}

const supplyKeys: Record<"many" | "one" | "none", MessageKey> = { many: "fcSupplyMany", one: "fcSupplyOne", none: "fcSupplyNone" };
const needKeys: Record<"many" | "one" | "none", MessageKey> = { many: "fcNeedMany", one: "fcNeedOne", none: "fcNeedNone" };

/** The calculation spelled out for the direction's busiest day, or its first day when none is short. */
function WorkedExample({ dir, data, lang }: { dir: DirectionForecast; data: GetFestivalForecastResponse; lang: Lang }) {
  const { t } = useI18n();
  const day = dir.days.find((d) => d.date === dir.peakDate) ?? dir.days.reduce((a, b) => (b.forecast > a.forecast ? b : a), dir.days[0]);
  if (!day) return null;

  const n = (v: number) => formatNumber(v, lang);
  const v = explainVariant(day, data.baselineTo, data.festival?.startsOn ?? "");
  const vars = {
    date: formatIsoDate(day.date, lang),
    weekday: t(weekdayLabels[day.weekday]),
    baseline: n(day.baseline),
    uplift: formatUplift(day.upliftBp, lang),
    forecast: n(day.forecast),
    departures: n(day.departures),
    capacity: n(day.capacity),
    extra: n(day.extraDepartures),
    load: formatBp(data.targetLoadBp, lang),
  };

  return (
    <span className="block">
      {t(v.change === "dip" ? "fcWorkedDip" : "fcWorkedRise", vars)} {t(supplyKeys[v.supply], vars)} {t(needKeys[v.need], vars)}
    </span>
  );
}
